import type { PurchasableCreditType } from "@repo/app-types/entities/creditUsage";
import { CREDIT_PACK_SUBSCRIPTION_OFFERS } from "@repo/app-types/helpers/billing/creditPackSubscription";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Modal, ModalSize } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { IconCreditCard } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { usePurchaseCreditPackSubscriptionMutation } from "../../../api/entities/company.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { formatWholeNumber } from "./usageFormatting.js";

interface PurchaseCreditPackModalProps {
  creditType: PurchasableCreditType;
  /** Exclusive ISO end of the current billing period; dates the renewal copy. */
  currentPeriodEnd?: string;
  isOpen: boolean;
  onClose: () => void;
}

/** How each credit type is named to the customer when buying a pack. */
const PACK_COPY_BY_CREDIT_TYPE: Record<
  PurchasableCreditType,
  { title: string; addedNoun: string }
> = {
  ai_credits: {
    title: "Buy credit pack",
    addedNoun: "AI credits",
  },
};

const formatUsd = (amount: number): string =>
  amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Self-serve credit pack subscription at list price: the pack is added to the
 * company's subscription and refills each billing period. This period's
 * prorated share is invoiced now and spendable at once, before the invoice is
 * paid.
 */
export const PurchaseCreditPackModal: FC<PurchaseCreditPackModalProps> = (
  props,
) => {
  const { creditType, currentPeriodEnd, isOpen, onClose } = props;
  const { unitPriceUsd, presetQuantities, minQuantity, maxQuantity } =
    CREDIT_PACK_SUBSCRIPTION_OFFERS[creditType];
  const copy = PACK_COPY_BY_CREDIT_TYPE[creditType];
  const dispatch = useAppDispatch();

  const defaultQuantity =
    presetQuantities[1] ?? presetQuantities[0] ?? minQuantity;
  // Either a preset size or a free-typed custom amount. The custom field keeps
  // its own text so an in-progress edit is not clobbered by the preset value,
  // and opens on whatever was last selected.
  const [selection, setSelection] = useState<number | "other">(defaultQuantity);
  const [customText, setCustomText] = useState(String(defaultQuantity));
  // One request id per open modal, so a retried click can never buy twice.
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [purchase, { isLoading }] = usePurchaseCreditPackSubscriptionMutation();

  const isOther = selection === "other";
  const parsedCustomQuantity = Number(customText);
  const quantity = isOther
    ? customText.trim() !== "" && Number.isSafeInteger(parsedCustomQuantity)
      ? parsedCustomQuantity
      : null
    : selection;
  const isValid =
    quantity !== null && quantity >= minQuantity && quantity <= maxQuantity;
  const displayedQuantity = quantity ?? 0;
  const priceLabel = formatUsd(displayedQuantity * unitPriceUsd);
  const resetDateLabel = currentPeriodEnd
    ? DateTime.fromISO(currentPeriodEnd, { zone: "utc" }).toFormat("MMM d")
    : null;

  const handleClose = () => {
    setRequestId(crypto.randomUUID());
    onClose();
  };

  const handleConfirm = async () => {
    if (!isValid || quantity === null) {
      return;
    }
    try {
      await purchase({ requestId, creditType, quantity }).unwrap();
      showNotification(dispatch, {
        id: `credit-pack-added-${requestId}`,
        primaryMessage: `${formatWholeNumber(quantity)} ${copy.addedNoun} added`,
        secondaryMessage: "They refill each billing period.",
        type: NotificationType.SUCCESS,
      });
      handleClose();
    } catch {
      showNotification(dispatch, {
        id: `credit-pack-failed-${requestId}`,
        primaryMessage: "The purchase could not be completed",
        secondaryMessage: "Contact support before trying again.",
        type: NotificationType.FAILURE,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onCancel={handleClose} size={ModalSize.Small}>
      <ModalHeader isCloseVisible>{copy.title}</ModalHeader>
      <p className="mt-2 text-sm text-muted-foreground">
        Credits added to your plan and refilled every billing period, at{" "}
        {formatUsd(unitPriceUsd)} each.
      </p>

      <p className="mt-5 text-sm font-medium text-foreground">
        Credits per billing period
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        {presetQuantities.map((preset) => (
          <button
            aria-pressed={!isOther && preset === selection}
            className={twMerge(
              "rounded-lg border px-4 py-3 text-left transition-colors",
              !isOther && preset === selection
                ? "border-indigo-600 bg-indigo-50"
                : "border-border-light bg-white hover:bg-gray-50",
            )}
            key={preset}
            onClick={() => setSelection(preset)}
            type="button"
          >
            <p className="text-sm font-semibold text-foreground">
              {formatWholeNumber(preset)}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatUsd(preset * unitPriceUsd)}/period
            </p>
          </button>
        ))}
      </div>
      <button
        aria-pressed={isOther}
        className={twMerge(
          "mt-2 w-full rounded-lg border px-4 py-3 text-left transition-colors",
          isOther
            ? "border-indigo-600 bg-indigo-50"
            : "border-border-light bg-white hover:bg-gray-50",
        )}
        onClick={() => {
          setCustomText(String(displayedQuantity));
          setSelection("other");
        }}
        type="button"
      >
        <p className="text-sm font-semibold text-foreground">Other amount</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Enter a custom number of credits
        </p>
      </button>
      {isOther ? (
        <div className="mt-2">
          <Input
            accessibleLabel="Custom credits per billing period"
            type="number"
            min={minQuantity}
            max={maxQuantity}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder={String(defaultQuantity)}
            errorDescription={
              customText !== "" && !isValid
                ? `Enter between ${formatWholeNumber(minQuantity)} and ${formatWholeNumber(maxQuantity)} credits.`
                : undefined
            }
          />
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-1.5 rounded-lg border border-border-light bg-slate-50 p-3">
        <div className="flex items-baseline justify-between gap-4 text-sm font-semibold text-foreground">
          <span>{formatWholeNumber(displayedQuantity)} credits / period</span>
          <span>{priceLabel}</span>
        </div>
        <div className="flex items-baseline justify-between gap-4 text-xs">
          <span className="text-muted-foreground">Billed now</span>
          <span className="text-right text-foreground">
            {resetDateLabel
              ? `Prorated through ${resetDateLabel}`
              : "Prorated this period"}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-4 text-xs">
          <span className="text-muted-foreground">At renewal</span>
          <span className="text-right text-foreground">
            {resetDateLabel
              ? `Full ${priceLabel} on ${resetDateLabel}`
              : `Full ${priceLabel}`}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Credits are valid for 12 months.
        </p>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          isDisabled={isLoading}
          onClick={handleClose}
          variant={ButtonVariant.Secondary}
        >
          Cancel
        </Button>
        <Button
          icon={<IconCreditCard className="size-4" />}
          isDisabled={!isValid}
          isLoading={isLoading}
          onClick={handleConfirm}
          variant={ButtonVariant.Primary}
        >
          Buy credits
        </Button>
      </div>
    </Modal>
  );
};
